import { useState, useEffect } from 'react';

const useLocalStorage = (key, initialValue) => {
	const [storedValue, setStoredValue] = useState(() => {
		try {
			const item = window.localStorage.getItem(key);
			return item ? JSON.parse(item) : initialValue;
		} catch (error) {
			console.error('Error reading from localStorage', error);
			return initialValue;
		}
	});

	// Add useEffect to keep the state in sync with changes in localStorage
	useEffect(() => {
		const handleStorageChange = () => {
			try {
				const item = window.localStorage.getItem(key);
				setStoredValue(item ? JSON.parse(item) : initialValue);
			} catch (error) {
				console.error('Error syncing localStorage', error);
			}
		};

		// Listen for changes to localStorage across the window
		window.addEventListener('storage', handleStorageChange);

		// Also watch for changes in the same tab (e.g., via setItem)
		return () => {
			window.removeEventListener('storage', handleStorageChange);
		};
	}, [key, initialValue]);

	const setValue = (value) => {
		try {
			const valueToStore = value instanceof Function ? value(storedValue) : value;
			setStoredValue(valueToStore);
			window.localStorage.setItem(key, JSON.stringify(valueToStore));
		} catch (error) {
			console.error('Error setting localStorage value', error);
		}
	};

	const removeLocalStorage = () => {
		if (!key) throw Error('Key is missing :(');
		localStorage.removeItem(key);
	};

	return { storage: storedValue, addLocalStorage: setValue, removeLocalStorage };
};

export default useLocalStorage;
